import { datadogRum } from '@datadog/browser-rum';
import { environmentsWithDatadog, getEnvironmentName } from 'src/AppUtils';
import {
  EventName,
  EventProperties,
  NavigationEventProperties,
  createEventProperties,
  NAVIGATION_EVENTS,
} from './eventTracking';

// ----------------------------------------------------------------------

// Types
interface User {
  id: string | number;
  username: string;
  email: string;
  role?: string;
}

interface DatadogConfig {
  applicationId: string;
  clientToken: string;
  site: string;
  service: string;
  env: string;
  version: string;
  sampleRate: number;
  premiumSampleRate: number;
  trackUserInteractions: boolean;
  trackResources: boolean;
  trackLongTasks: boolean;
  defaultPrivacyLevel: 'allow' | 'mask' | 'mask-user-input';
  allowedTracingOrigins: string[];
}

// Check if Datadog should be enabled for current environment
export const isDatadogEnabled = (): boolean => {
  return environmentsWithDatadog.includes(getEnvironmentName());
};

// Check if Datadog RUM is properly initialized and available
export const isDatadogRumAvailable = (): boolean => {
  try {
    if (typeof window === 'undefined') {
      return false;
    }

    if (!datadogRum) {
      return false;
    }

    const sessionInfo = datadogRum.getInternalContext();
    return sessionInfo !== undefined && sessionInfo !== null;
  } catch {
    return false;
  }
};

// Initialize Datadog RUM
export const initializeDatadogRum = (): void => {
  if (!isDatadogEnabled()) {
    return;
  }

  if (!import.meta.env.VITE_DATADOG_ID || !import.meta.env.VITE_DATADOG_TOKEN) {
    return;
  }

  const config: DatadogConfig = {
    applicationId: import.meta.env.VITE_DATADOG_ID || '',
    clientToken: import.meta.env.VITE_DATADOG_TOKEN || '',
    site: 'datadoghq.com',
    service: 'loki',
    env: getEnvironmentName(),
    version: import.meta.env.VITE_VERSION || '1.0.0',
    sampleRate: 100,
    premiumSampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'allow',
    allowedTracingOrigins: [
      import.meta.env.VITE_API_ROOT,
      /https:\/\/(www\.|dev\.|stage\.)?prontohousingrentals\.com/,
    ].filter(Boolean),
  };

  datadogRum.init(config);

  // DO NOT REMOVE: Session replay recording doesn't trigger automatically for v4.5
  datadogRum.startSessionReplayRecording();
};

// Set user identification for tracking
export const setUserIdentification = (user: User): void => {
  if (!isDatadogEnabled() || !isDatadogRumAvailable()) {
    return;
  }

  try {
    datadogRum.setUser({
      id: user.id.toString(),
      name: user.username,
      email: user.email,
      usertype: user.role || 'unknown',
    });
  } catch {
    // Silent error handling
  }
};

// Main event tracking function
export const trackEvent = (
  eventName: EventName,
  payload: EventProperties = {}
): void => {
  // Early return if Datadog is not available - don't break the UI
  if (!isDatadogEnabled()) {
    return;
  }

  // Check if Datadog RUM is available without throwing
  let isRumAvailable = false;
  try {
    isRumAvailable = isDatadogRumAvailable();
  } catch {
    // Silently fail if we can't check RUM availability
    return;
  }

  if (!isRumAvailable) {
    return;
  }

  try {
    const eventData = {
      ...payload,
      timestamp: new Date().toISOString(),
      environment: getEnvironmentName(),
    };

    // Send via RUM
    datadogRum.addAction(eventName, eventData);
  } catch {
    // Silent error handling
  }
};

// Navigation tracking functions
export const trackNavigation = (
  eventName: (typeof NAVIGATION_EVENTS)[keyof typeof NAVIGATION_EVENTS],
  properties: Parameters<typeof createEventProperties>[0] = {}
): void => {
  try {
    trackEvent(eventName, createEventProperties(properties));
  } catch {
    // Silent error handling
  }
};

// Header icon click tracking
export const trackHeaderIconClick = (
  iconName: string,
  properties: Parameters<typeof createEventProperties>[0] = {}
): void => {
  try {
    trackNavigation(NAVIGATION_EVENTS.HEADER_ICON_CLICK, {
      icon_name: iconName,
      navigation_method: 'header',
      ...properties,
    } as NavigationEventProperties);
  } catch {
    // Silent error handling
  }
};

// Error tracking function
export const trackError = (
  error: Error | string,
  context: Record<string, unknown> = {}
): void => {
  if (!isDatadogEnabled() || !isDatadogRumAvailable()) {
    return;
  }

  try {
    const errorData = {
      ...context,
      environment: getEnvironmentName(),
    };

    datadogRum.addError(error, errorData);
  } catch {
    // Silent error handling
  }
};

// Performance tracking function
export const trackPerformance = (metric: string, value: number): void => {
  if (!isDatadogEnabled() || !isDatadogRumAvailable()) {
    return;
  }

  try {
    datadogRum.addTiming(metric, value);
  } catch {
    // Silent error handling
  }
};

// Default export for backward compatibility
const track = {
  pageview: (props: Record<string, unknown>) => {
    trackNavigation(NAVIGATION_EVENTS.PAGE_VIEW, {
      page: props.page_path as string,
      navigation_method: 'direct',
    });
  },
  event: (type: string, props: Record<string, unknown>) => {
    trackEvent(type as EventName, props as EventProperties);
  },
};

export default track;

// Navigation click tracking (for sidebar navigation)
export const trackNavigationClick = (
  page: string,
  navigationItem: string
): void => {
  trackNavigation(NAVIGATION_EVENTS.PAGE_VIEW, {
    page,
    navigation_method: 'sidebar',
    navigation_item: navigationItem,
  });
};
